import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { styled } from "@mui/material/styles";
import { Panel } from "../../../primitives/surfaces/Panel";
import { Note } from "../../../primitives/data-display/Note";
import { gaps } from "../../../primitives/spacing";
import { nameInitials } from "../../../../utils/user";

const ExpertAvatar = styled(Avatar)({ width: 32, height: 32 });

interface InsightEntry {
  rank: number;
  title: string;
  body: string;
}

interface ExpertInsightCardProps {
  /** Expert attribution — always present; unattributed insights are not displayed. */
  expertName: string;
  expertCredentials: string | null;
  /** Short bio shown in the info tooltip. Omits the icon when null. */
  expertBio: string | null;
  expertAvatarUrl: string | null;
  /** 1–N insights from this expert, in ranked order. */
  insights: InsightEntry[];
}

/** Renders one or more expert insights from the same expert as an attributed panel.
 *  Visually and semantically separate from the evidence that produced the verdict. */
export function ExpertInsightCard({
  expertName,
  expertCredentials,
  expertBio,
  expertAvatarUrl,
  insights,
}: ExpertInsightCardProps) {
  return (
    <Panel>
      <Stack gap={gaps.base}>
        <ListItem
          disablePadding
          secondaryAction={
            expertBio ? (
              <Tooltip title={expertBio} placement="top" arrow>
                <IconButton size="small" edge="end" aria-label="About this expert">
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : undefined
          }
        >
          <ListItemAvatar>
            <ExpertAvatar src={expertAvatarUrl ?? undefined} alt={expertName}>
              {nameInitials(expertName)}
            </ExpertAvatar>
          </ListItemAvatar>
          <ListItemText
            primary={expertName}
            secondary={expertCredentials ?? undefined}
            primaryTypographyProps={{ variant: "body2" }}
            secondaryTypographyProps={{ variant: "caption" }}
          />
        </ListItem>

        <Stack gap={gaps.base} divider={<Divider />}>
          {insights.map((insight) => (
            <Stack key={insight.rank} gap={gaps.tight}>
              <Typography variant="body2" fontWeight={600} color="text.primary">
                {insight.title}
              </Typography>
              <Note>{insight.body}</Note>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Panel>
  );
}
